import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
const KEY = 'gh.theme';

interface ThemeCtx {
  pref: ThemePref;
  resolved: 'light' | 'dark';
  setPref: (p: ThemePref) => void;
  toggle: () => void;
}
const Ctx = createContext<ThemeCtx | null>(null);

/**
 * Customer-facing form pages (`/f/…`, `/s/…`) look the same everywhere: the owner's light theme,
 * also for their loading/error states and dialogs, whatever the visitor's dark-mode setting.
 * Mirrored by the inline script in index.html (before first paint).
 */
export function forcedThemeFor(pathname: string): 'light' | null {
  return /^\/(f|s)\//.test(pathname) ? 'light' : null;
}

function systemDark() {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    try {
      return (localStorage.getItem(KEY) as ThemePref) || 'system';
    } catch {
      return 'system';
    }
  });
  const [sysDark, setSysDark] = useState(systemDark);

  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const on = () => setSysDark(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const forced = typeof window !== 'undefined' ? forcedThemeFor(window.location.pathname) : null;
  const resolved: 'light' | 'dark' =
    forced ?? (pref === 'system' ? (sysDark ? 'dark' : 'light') : pref);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try {
      localStorage.setItem(KEY, p);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({
      pref,
      resolved,
      setPref,
      toggle: () => setPref(resolved === 'dark' ? 'light' : 'dark'),
    }),
    [pref, resolved, setPref],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTheme must be used inside <ThemeProvider>');
  return c;
}
